import {
  Box,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RiskAssessmentService } from 'services/RiskAssessmentService';
import { PublicActions } from 'state/public/Action';
import RiskAssessmentDetail from 'modules/risk-management/risk-assessments/risk-assessments-detail/components/RiskAssessmentDetail';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { IAssessment } from 'modules/risk-management/risk-assessments/Main';

const Main: React.FC = () => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const [viewMode, setViewMode] = useState<
    'loading' | 'error' | 'assessment' | 'success' | 'submitted'
  >('loading');
  const storeDispatch = useDispatch();
  const [assessmentDetail, setAssessmentDetail] = useState<IAssessment>();

  // API Calls
  const loadAssessmentDetail = (): void => {
    setIsAuthenticating(true);
    RiskAssessmentService.getGuestExternalAssessmentDetail()
      .then((response) => {
        const { data } = response.data;
        if (data.length > 0) {
          const assessment: IAssessment = data[0];
          setAssessmentDetail(assessment);
          if (assessment.status === 'Completed') {
            setViewMode('submitted');
          } else {
            setViewMode('assessment');
          }

          setIsAuthenticating(false);
        }
      })
      .catch(() => {
        setViewMode('submitted');
        setIsAuthenticating(false);
      });
  };

  const validateRequest = (uniqueId: string): void => {
    setIsAuthenticating(true);
    RiskAssessmentService.getExternalAuthToken({ uniqueId })
      .then((response) => {
        const { data } = response.data;
        storeDispatch(
          PublicActions.setLoginStatus({ accessToken: data.authToken })
        );
        setTimeout(() => {
          loadAssessmentDetail();
        }, 500);
      })
      .catch(() => {
        setViewMode('error');
        setIsAuthenticating(false);
      });
  };

  // Hooks
  useEffect(() => {
    if (queryParams.uniqueId) {
      validateRequest(`${queryParams.uniqueId}`);
    }
  }, [queryParams?.uniqueId]);

  // UI Elements
  return (
    <>
      {isAuthenticating && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}

      {!isAuthenticating && viewMode === 'assessment' && assessmentDetail && (
        <Container maxWidth="md" className="mt-5">
          <Stack
            spacing={2}
            sx={{
              border: '1px solid lightgray',
              borderRadius: '5px',
              mx: 6,
              p: 5,
            }}
          >
            <Typography variant="h2">Risk Assessment</Typography>
            <Box>
              <Typography variant="body2" className="text-gray">
                {assessmentDetail.entityType}
              </Typography>
              <Typography>
                {assessmentDetail.entityType === 'AI System'
                  ? assessmentDetail.aisystems?.solutionName
                  : assessmentDetail.dataset?.datasetName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" className="text-gray">
                Requested By:
              </Typography>
              <Typography>{assessmentDetail.requester.name}</Typography>
              <Typography>{assessmentDetail.requester.email}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" className="text-gray">
                Assessment
              </Typography>
              <Typography>
                {assessmentDetail.template[0].templateName}
              </Typography>
            </Box>
          </Stack>
          <RiskAssessmentDetail
            guestMode
            aiSystems={
              assessmentDetail.aiInventoryId
                ? [
                    {
                      _id: assessmentDetail.aiInventoryId,
                      title: assessmentDetail.aiInventoryId,
                    },
                  ]
                : []
            }
            datasets={
              assessmentDetail.datasetId
                ? [
                    {
                      _id: assessmentDetail.datasetId,
                      title: assessmentDetail.datasetId,
                    },
                  ]
                : []
            }
            assessmentIds={[assessmentDetail._id]}
            templateDetail={assessmentDetail.template[0]}
            quickEdit
            userResponses={{}}
            onFinish={() => {
              setViewMode('success');
            }}
          />
        </Container>
      )}

      {viewMode === 'error' && (
        <Container maxWidth="sm" className="mt-5">
          <Stack
            spacing={2}
            sx={{
              border: '1px solid lightgray',
              borderRadius: '5px',
              mx: 6,
              p: 5,
            }}
            alignItems="center"
          >
            <CancelOutlinedIcon color="error" sx={{ fontSize: 40 }} />
            <Typography variant="h3">Unable to process this request</Typography>
          </Stack>
        </Container>
      )}

      {!isAuthenticating && viewMode === 'success' && (
        <Container maxWidth="sm" className="mt-5">
          <Stack
            spacing={2}
            sx={{
              border: '1px solid lightgray',
              borderRadius: '5px',
              mx: 6,
              p: 5,
            }}
            alignItems="center"
          >
            <CheckCircleOutlinedIcon sx={{ color: 'green', fontSize: 40 }} />
            <Typography variant="h3">Assessment Submitted</Typography>
            <Typography variant="body1">
              Thank you for completing the risk assessment. Your responses have
              been recorded
            </Typography>
          </Stack>
        </Container>
      )}

      {!isAuthenticating && viewMode === 'submitted' && (
        <Container maxWidth="sm" className="mt-5">
          <Stack
            spacing={2}
            sx={{
              border: '1px solid lightgray',
              borderRadius: '5px',
              mx: 6,
              p: 5,
            }}
            alignItems="center"
          >
            <CheckCircleOutlinedIcon sx={{ color: 'green', fontSize: 40 }} />
            <Typography variant="h3">Already Completed</Typography>
            <Typography variant="body1">
              This assessment has already been completed
            </Typography>
          </Stack>
        </Container>
      )}
    </>
  );
};

export default Main;
